import { PageGen, PdfBrush } from '../pdf';

// ---------------------------------------------------------------------------
// Symbol shape definition
// ---------------------------------------------------------------------------

export interface SymbolPart {
  path: string;
  fill: boolean;
  thick: number; // absolute thickness
}

export interface SymbolShape {
  w: number; // total width
  h: number; // height above baseline
  d: number; // depth below baseline
  parts: SymbolPart[];
}

// Symbol drawing function: (leftX, baselineY, size) → shape
export type SymbolFn = (x: number, b: number, s: number) => SymbolShape;

// ---------------------------------------------------------------------------
// Low-level helpers
// ---------------------------------------------------------------------------

/** Horizontal line at y */
function hline(x1: number, x2: number, y: number): string {
  return `M ${x1} ${y} H ${x2}`;
}
/** Filled right-pointing arrowhead (tip at (tx, ty)) */
function aheadR(tx: number, ty: number, hw: number, hh: number): string {
  return `M ${tx - hw} ${ty - hh} L ${tx} ${ty} L ${tx - hw} ${ty + hh} Z`;
}
/** Filled left-pointing arrowhead (tip at (tx, ty)) */
function aheadL(tx: number, ty: number, hw: number, hh: number): string {
  return `M ${tx + hw} ${ty - hh} L ${tx} ${ty} L ${tx + hw} ${ty + hh} Z`;
}
/** Filled up-pointing arrowhead (tip at (tx, ty)) */
function aheadU(tx: number, ty: number, hw: number, hh: number): string {
  return `M ${tx - hw} ${ty + hh} L ${tx} ${ty} L ${tx + hw} ${ty + hh} Z`;
}
/** Filled down-pointing arrowhead (tip at (tx, ty)) */
function aheadD(tx: number, ty: number, hw: number, hh: number): string {
  return `M ${tx - hw} ${ty - hh} L ${tx} ${ty} L ${tx + hw} ${ty - hh} Z`;
}

/** Simple single horizontal arrow (right) */
function hArrowR(x: number, ay: number, s: number, w: number, t: number): SymbolPart[] {
  const hw = 0.15 * s;
  const hh = 0.09 * s;
  return [
    { path: `M ${x} ${ay} H ${x + w - hw}`, fill: false, thick: t },
    { path: aheadR(x + w, ay, hw, hh), fill: true, thick: 0 },
  ];
}
function hArrowL(x: number, ay: number, s: number, w: number, t: number): SymbolPart[] {
  const hw = 0.15 * s;
  const hh = 0.09 * s;
  return [
    { path: `M ${x + hw} ${ay} H ${x + w}`, fill: false, thick: t },
    { path: aheadL(x, ay, hw, hh), fill: true, thick: 0 },
  ];
}
function hArrowLR(x: number, ay: number, s: number, w: number, t: number): SymbolPart[] {
  const hw = 0.15 * s;
  const hh = 0.09 * s;
  return [
    { path: `M ${x + hw} ${ay} H ${x + w - hw}`, fill: false, thick: t },
    { path: aheadR(x + w, ay, hw, hh), fill: true, thick: 0 },
    { path: aheadL(x, ay, hw, hh), fill: true, thick: 0 },
  ];
}
function dblArrowR(x: number, ay: number, s: number, w: number, t: number): SymbolPart[] {
  const hw = 0.14 * s;
  const hh = 0.11 * s;
  const gap = 0.06 * s;
  return [
    { path: `M ${x} ${ay - gap} H ${x + w - hw}`, fill: false, thick: t },
    { path: `M ${x} ${ay + gap} H ${x + w - hw}`, fill: false, thick: t },
    { path: aheadR(x + w, ay, hw, hh + gap), fill: true, thick: 0 },
    { path: `M ${x} ${ay - gap} V ${ay + gap}`, fill: false, thick: t },
  ];
}
function dblArrowL(x: number, ay: number, s: number, w: number, t: number): SymbolPart[] {
  const hw = 0.14 * s;
  const hh = 0.11 * s;
  const gap = 0.06 * s;
  return [
    { path: `M ${x + hw} ${ay - gap} H ${x + w}`, fill: false, thick: t },
    { path: `M ${x + hw} ${ay + gap} H ${x + w}`, fill: false, thick: t },
    { path: aheadL(x, ay, hw, hh + gap), fill: true, thick: 0 },
    { path: `M ${x + w} ${ay - gap} V ${ay + gap}`, fill: false, thick: t },
  ];
}
function dblArrowLR(x: number, ay: number, s: number, w: number, t: number): SymbolPart[] {
  const hw = 0.14 * s;
  const hh = 0.11 * s;
  const gap = 0.06 * s;
  return [
    { path: `M ${x + hw} ${ay - gap} H ${x + w - hw}`, fill: false, thick: t },
    { path: `M ${x + hw} ${ay + gap} H ${x + w - hw}`, fill: false, thick: t },
    { path: aheadR(x + w, ay, hw, hh + gap), fill: true, thick: 0 },
    { path: aheadL(x, ay, hw, hh + gap), fill: true, thick: 0 },
  ];
}

// ---------------------------------------------------------------------------
// Symbol registry
// ---------------------------------------------------------------------------

const W = 0.75; // standard arrow width (em fraction)
const WT = 0.55; // standard relation width
const T = 0.05; // standard line thickness (em fraction)

/** axis height above baseline (em fraction) */
const AX = 0.28;

export const DRAWN_SYMBOLS: Record<string, SymbolFn> = {
  // -------------------------------------------------------------------------
  // Simple arrows
  // -------------------------------------------------------------------------

  rightarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: hArrowR(x, ay, s, w, t) };
  },
  leftarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: hArrowL(x, ay, s, w, t) };
  },
  leftrightarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: hArrowLR(x, ay, s, w, t) };
  },
  uparrow: (x, b, s) => {
    const t = T * s;
    const w = 0.4 * s;
    const cx = x + w / 2;
    const top = b - 0.7 * s;
    const bot = b;
    const hw = 0.12 * s;
    const hh = 0.18 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0,
      parts: [
        { path: `M ${cx} ${bot} V ${top + hh}`, fill: false, thick: t },
        { path: aheadU(cx, top, hw, hh), fill: true, thick: 0 },
      ],
    };
  },
  downarrow: (x, b, s) => {
    const t = T * s;
    const w = 0.4 * s;
    const cx = x + w / 2;
    const top = b - 0.7 * s;
    const bot = b;
    const hw = 0.12 * s;
    const hh = 0.18 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0,
      parts: [
        { path: `M ${cx} ${top} V ${bot - hh}`, fill: false, thick: t },
        { path: aheadD(cx, bot, hw, hh), fill: true, thick: 0 },
      ],
    };
  },
  updownarrow: (x, b, s) => {
    const t = T * s;
    const w = 0.4 * s;
    const cx = x + w / 2;
    const top = b - 0.7 * s;
    const bot = b;
    const hw = 0.12 * s;
    const hh = 0.18 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0,
      parts: [
        { path: `M ${cx} ${top + hh} V ${bot - hh}`, fill: false, thick: t },
        { path: aheadU(cx, top, hw, hh), fill: true, thick: 0 },
        { path: aheadD(cx, bot, hw, hh), fill: true, thick: 0 },
      ],
    };
  },

  // -------------------------------------------------------------------------
  // Double arrows
  // -------------------------------------------------------------------------

  Rightarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: dblArrowR(x, ay, s, w, t) };
  },
  Leftarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: dblArrowL(x, ay, s, w, t) };
  },
  Leftrightarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: dblArrowLR(x, ay, s, w, t) };
  },
  Uparrow: (x, b, s) => {
    const t = T * s;
    const w = 0.45 * s;
    const cx = x + w / 2;
    const top = b - 0.7 * s;
    const bot = b;
    const gap = 0.07 * s;
    const hh = 0.18 * s;
    const hw = 0.14 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0,
      parts: [
        { path: `M ${cx - gap} ${bot} V ${top + hh}`, fill: false, thick: t },
        { path: `M ${cx + gap} ${bot} V ${top + hh}`, fill: false, thick: t },
        { path: aheadU(cx, top, hw, hh), fill: true, thick: 0 },
        { path: `M ${cx - gap} ${bot} H ${cx + gap}`, fill: false, thick: t },
      ],
    };
  },
  Downarrow: (x, b, s) => {
    const t = T * s;
    const w = 0.45 * s;
    const cx = x + w / 2;
    const top = b - 0.7 * s;
    const bot = b;
    const gap = 0.07 * s;
    const hh = 0.18 * s;
    const hw = 0.14 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0,
      parts: [
        { path: `M ${cx - gap} ${top} V ${bot - hh}`, fill: false, thick: t },
        { path: `M ${cx + gap} ${top} V ${bot - hh}`, fill: false, thick: t },
        { path: aheadD(cx, bot, hw, hh), fill: true, thick: 0 },
        { path: `M ${cx - gap} ${top} H ${cx + gap}`, fill: false, thick: t },
      ],
    };
  },

  // -------------------------------------------------------------------------
  // Diagonal arrows
  // -------------------------------------------------------------------------

  nearrow: (x, b, s) => {
    const t = T * s;
    const w = 0.7 * s;
    const h = 0.65 * s;
    const hw = 0.12 * s;
    const hh = 0.08 * s;
    const len = Math.hypot(w - hw, h - hw);
    const dx = ((w - hw) * hw) / len;
    const dy = ((h - hw) * hw) / len;
    return {
      w,
      h,
      d: 0.05 * s,
      parts: [
        { path: `M ${x} ${b} L ${x + w - dx} ${b - h + dy}`, fill: false, thick: t },
        {
          path: `M ${x + w - hw} ${b - h + hh} L ${x + w} ${b - h} L ${x + w - hh} ${b - h + hw} Z`,
          fill: true,
          thick: 0,
        },
      ],
    };
  },
  searrow: (x, b, s) => {
    const t = T * s;
    const w = 0.7 * s;
    const h = 0.65 * s;
    const hw = 0.12 * s;
    const hh = 0.08 * s;
    return {
      w,
      h: 0.65 * s,
      d: 0,
      parts: [
        { path: `M ${x} ${b - h} L ${x + w - hw} ${b - hw}`, fill: false, thick: t },
        {
          path: `M ${x + w - hw} ${b - hh} L ${x + w} ${b} L ${x + w - hh} ${b - hw} Z`,
          fill: true,
          thick: 0,
        },
      ],
    };
  },
  swarrow: (x, b, s) => {
    const t = T * s;
    const w = 0.7 * s;
    const h = 0.65 * s;
    const hw = 0.12 * s;
    const hh = 0.08 * s;
    return {
      w,
      h: 0.65 * s,
      d: 0,
      parts: [
        { path: `M ${x + w} ${b - h} L ${x + hw} ${b - hw}`, fill: false, thick: t },
        { path: `M ${x + hh} ${b - hw} L ${x} ${b} L ${x + hw} ${b - hh} Z`, fill: true, thick: 0 },
      ],
    };
  },
  nwarrow: (x, b, s) => {
    const t = T * s;
    const w = 0.7 * s;
    const h = 0.65 * s;
    const hw = 0.12 * s;
    const hh = 0.08 * s;
    return {
      w,
      h,
      d: 0.05 * s,
      parts: [
        { path: `M ${x + w} ${b} L ${x + hw} ${b - h + hw}`, fill: false, thick: t },
        {
          path: `M ${x + hh} ${b - h + hw} L ${x} ${b - h} L ${x + hw} ${b - h + hh} Z`,
          fill: true,
          thick: 0,
        },
      ],
    };
  },

  // -------------------------------------------------------------------------
  // Long arrows
  // -------------------------------------------------------------------------

  longrightarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = 1.3 * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: hArrowR(x, ay, s, w, t) };
  },
  longleftarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = 1.3 * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: hArrowL(x, ay, s, w, t) };
  },
  longleftrightarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = 1.3 * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: hArrowLR(x, ay, s, w, t) };
  },
  Longrightarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = 1.3 * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: dblArrowR(x, ay, s, w, t) };
  },
  Longleftarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = 1.3 * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: dblArrowL(x, ay, s, w, t) };
  },
  Longleftrightarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = 1.3 * s;
    const t = T * s;
    return { w, h: (AX + 0.15) * s, d: 0.15 * s, parts: dblArrowLR(x, ay, s, w, t) };
  },

  // -------------------------------------------------------------------------
  // Harpoon arrows
  // -------------------------------------------------------------------------

  rightharpoonup: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    const hw = 0.14 * s;
    const hh = 0.09 * s;
    return {
      w,
      h: (AX + 0.15) * s,
      d: 0.05 * s,
      parts: [
        { path: `M ${x} ${ay} H ${x + w - hw}`, fill: false, thick: t },
        {
          path: `M ${x + w - hw} ${ay - hh} L ${x + w} ${ay} L ${x + w - hw} ${ay} Z`,
          fill: true,
          thick: 0,
        },
      ],
    };
  },
  rightharpoondown: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    const hw = 0.14 * s;
    const hh = 0.09 * s;
    return {
      w,
      h: (AX + 0.05) * s,
      d: 0.15 * s,
      parts: [
        { path: `M ${x} ${ay} H ${x + w - hw}`, fill: false, thick: t },
        {
          path: `M ${x + w - hw} ${ay} L ${x + w} ${ay} L ${x + w - hw} ${ay + hh} Z`,
          fill: true,
          thick: 0,
        },
      ],
    };
  },
  leftharpoonup: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    const hw = 0.14 * s;
    const hh = 0.09 * s;
    return {
      w,
      h: (AX + 0.15) * s,
      d: 0.05 * s,
      parts: [
        { path: `M ${x + hw} ${ay} H ${x + w}`, fill: false, thick: t },
        { path: `M ${x + hw} ${ay - hh} L ${x} ${ay} L ${x + hw} ${ay} Z`, fill: true, thick: 0 },
      ],
    };
  },
  rightleftharpoons: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    const hw = 0.13 * s;
    const hh = 0.08 * s;
    const g = 0.08 * s;
    return {
      w,
      h: (AX + 0.18) * s,
      d: 0.18 * s,
      parts: [
        // top: rightward harpoon
        { path: `M ${x} ${ay - g} H ${x + w - hw}`, fill: false, thick: t },
        {
          path: `M ${x + w - hw} ${ay - g - hh} L ${x + w} ${ay - g} L ${x + w - hw} ${ay - g} Z`,
          fill: true,
          thick: 0,
        },
        // bottom: leftward harpoon
        { path: `M ${x + hw} ${ay + g} H ${x + w}`, fill: false, thick: t },
        {
          path: `M ${x + hw} ${ay + g} L ${x} ${ay + g} L ${x + hw} ${ay + g + hh} Z`,
          fill: true,
          thick: 0,
        },
      ],
    };
  },
  mapsto: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    const parts = hArrowR(x + t * 1.5, ay, s, w - t * 1.5, t);
    parts.push({ path: `M ${x} ${ay - 0.15 * s} V ${ay + 0.15 * s}`, fill: false, thick: t * 1.5 });
    return { w, h: (AX + 0.2) * s, d: 0.2 * s, parts };
  },
  hookrightarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    const r = 0.1 * s;
    const parts = hArrowR(x + r * 2, ay, s, w - r * 2, t);
    // hook: small downward arc at left
    parts.push({
      path: `M ${x} ${ay - r} A ${r} ${r} 0 0 1 ${x + r * 2} ${ay}`,
      fill: false,
      thick: t,
    });
    return { w, h: (AX + 0.2) * s, d: 0.15 * s, parts };
  },
  hookleftarrow: (x, b, s) => {
    const ay = b - AX * s;
    const w = W * s;
    const t = T * s;
    const r = 0.1 * s;
    const parts = hArrowL(x, ay, s, w - r * 2, t);
    // hook at right
    parts.push({
      path: `M ${x + w} ${ay - r} A ${r} ${r} 0 0 0 ${x + w - r * 2} ${ay}`,
      fill: false,
      thick: t,
    });
    return { w, h: (AX + 0.2) * s, d: 0.15 * s, parts };
  },

  // -------------------------------------------------------------------------
  // Relations
  // -------------------------------------------------------------------------

  leq: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    const bar = 0.08 * s;
    return {
      w,
      h: AX * s + h + 0.05 * s,
      d: h + bar + 0.06 * s,
      parts: [
        { path: `M ${x + w} ${ay - h} L ${x} ${ay} L ${x + w} ${ay + h}`, fill: false, thick: t },
        { path: hline(x, x + w, ay + h + bar), fill: false, thick: t },
      ],
    };
  },
  geq: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    const bar = 0.08 * s;
    return {
      w,
      h: AX * s + h + 0.05 * s,
      d: h + bar + 0.06 * s,
      parts: [
        { path: `M ${x} ${ay - h} L ${x + w} ${ay} L ${x} ${ay + h}`, fill: false, thick: t },
        { path: hline(x, x + w, ay + h + bar), fill: false, thick: t },
      ],
    };
  },
  neq: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const g = 0.13 * s;
    return {
      w,
      h: (AX + 0.2) * s,
      d: 0.28 * s,
      parts: [
        { path: hline(x, x + w, ay - g), fill: false, thick: t },
        { path: hline(x, x + w, ay + g), fill: false, thick: t },
        {
          path: `M ${x + 0.12 * s} ${b + 0.12 * s} L ${x + w - 0.12 * s} ${b - 0.65 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  nleq: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    const bar = 0.08 * s;
    return {
      w,
      h: AX * s + h + 0.05 * s,
      d: h + bar + 0.06 * s,
      parts: [
        { path: `M ${x} ${ay - h} L ${x + w} ${ay} L ${x} ${ay + h}`, fill: false, thick: t },
        { path: hline(x, x + w, ay + h + bar), fill: false, thick: t },
        {
          path: `M ${x + 0.15 * s} ${b + 0.15 * s} L ${x + w - 0.15 * s} ${b - 0.7 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  ngeq: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    const bar = 0.08 * s;
    return {
      w,
      h: AX * s + h + 0.05 * s,
      d: h + bar + 0.06 * s,
      parts: [
        { path: `M ${x} ${ay - h} L ${x + w} ${ay} L ${x} ${ay + h}`, fill: false, thick: t },
        { path: hline(x, x + w, ay + h + bar), fill: false, thick: t },
        {
          path: `M ${x + 0.15 * s} ${b - 0.15 * s} L ${x + w - 0.15 * s} ${b + 0.7 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  nless: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    return {
      w,
      h: (AX + 0.2) * s,
      d: 0.28 * s,
      parts: [
        {
          path: `M ${x + w} ${b - 0.6 * s} L ${x} ${b} L ${x + w} ${b + 0.6 * s}`,
          fill: false,
          thick: t,
        },
        {
          path: `M ${x + 0.15 * s} ${b + 0.15 * s} L ${x + w - 0.15 * s} ${b - 0.7 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  ngtr: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    return {
      w,
      h: (AX + 0.2) * s,
      d: 0.28 * s,
      parts: [
        {
          path: `M ${x + w} ${b - 0.6 * s} L ${x} ${b} L ${x + w} ${b + 0.6 * s}`,
          fill: false,
          thick: t,
        },
        {
          path: `M ${x + 0.15 * s} ${b - 0.15 * s} L ${x + w - 0.15 * s} ${b + 0.7 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  nsim: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const amp = 0.09 * s;
    return {
      w,
      h: (AX + 0.2) * s,
      d: 0.25 * s,
      parts: [
        {
          path: `M ${x} ${ay} C ${x + w * 0.25} ${ay - amp} ${x + w * 0.75} ${ay + amp} ${x + w} ${ay}`,
          fill: false,
          thick: t,
        },
        {
          path: `M ${x + 0.12 * s} ${b + 0.12 * s} L ${x + w - 0.12 * s} ${b - 0.65 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  napprox: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const amp = 0.09 * s;
    function wave(y: number) {
      return `M ${x} ${y} C ${x + w * 0.25} ${y - amp} ${x + w * 0.75} ${y + amp} ${x + w} ${y}`;
    }
    return {
      w,
      h: (AX + 0.25) * s,
      d: 0.25 * s,
      parts: [
        { path: wave(ay - 0.1 * s), fill: false, thick: t },
        { path: wave(ay + 0.1 * s), fill: false, thick: t },
        {
          path: `M ${x + 0.12 * s} ${b + 0.12 * s} L ${x + w - 0.12 * s} ${b - 0.65 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  ncong: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const amp = 0.08 * s;
    return {
      w,
      h: (AX + 0.3) * s,
      d: 0.28 * s,
      parts: [
        {
          path: `M ${x} ${ay - 0.15 * s} C ${x + w * 0.25} ${ay - 0.15 * s - amp} ${x + w * 0.75} ${ay - 0.15 * s + amp} ${x + w} ${ay - 0.15 * s}`,
          fill: false,
          thick: t,
        },
        { path: hline(x, x + w, ay + 0.05 * s), fill: false, thick: t },
        { path: hline(x, x + w, ay + 0.18 * s), fill: false, thick: t },
        {
          path: `M ${x + 0.12 * s} ${b + 0.12 * s} L ${x + w - 0.12 * s} ${b - 0.65 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  neqv: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const g = 0.13 * s;
    return {
      w,
      h: (AX + 0.22) * s,
      d: 0.22 * s,
      parts: [
        { path: hline(x, x + w, ay - g), fill: false, thick: t },
        { path: hline(x, x + w, ay), fill: false, thick: t },
        { path: hline(x, x + w, ay + g), fill: false, thick: t },
        {
          path: `M ${x + 0.12 * s} ${b + 0.12 * s} L ${x + w - 0.12 * s} ${b - 0.65 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  approx: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const amp = 0.09 * s;
    function wave(y: number) {
      return `M ${x} ${y} C ${x + w * 0.25} ${y - amp} ${x + w * 0.75} ${y + amp} ${x + w} ${y}`;
    }
    return {
      w,
      h: (AX + 0.2) * s,
      d: 0.2 * s,
      parts: [
        { path: wave(ay - 0.1 * s), fill: false, thick: t },
        { path: wave(ay + 0.1 * s), fill: false, thick: t },
      ],
    };
  },
  equiv: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const g = 0.13 * s;
    return {
      w,
      h: (AX + 0.18) * s,
      d: 0.18 * s,
      parts: [
        { path: hline(x, x + w, ay - g), fill: false, thick: t },
        { path: hline(x, x + w, ay), fill: false, thick: t },
        { path: hline(x, x + w, ay + g), fill: false, thick: t },
      ],
    };
  },
  sim: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const amp = 0.09 * s;
    return {
      w,
      h: (AX + 0.15) * s,
      d: 0.15 * s,
      parts: [
        {
          path: `M ${x} ${ay} C ${x + w * 0.25} ${ay - amp} ${x + w * 0.75} ${ay + amp} ${x + w} ${ay}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  simeq: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const amp = 0.09 * s;
    return {
      w,
      h: (AX + 0.2) * s,
      d: 0.2 * s,
      parts: [
        {
          path: `M ${x} ${ay - 0.1 * s} C ${x + w * 0.25} ${ay - 0.1 * s - amp} ${x + w * 0.75} ${ay - 0.1 * s + amp} ${x + w} ${ay - 0.1 * s}`,
          fill: false,
          thick: t,
        },
        { path: hline(x, x + w, ay + 0.1 * s), fill: false, thick: t },
      ],
    };
  },
  cong: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const amp = 0.08 * s;
    return {
      w,
      h: (AX + 0.25) * s,
      d: 0.22 * s,
      parts: [
        {
          path: `M ${x} ${ay - 0.15 * s} C ${x + w * 0.25} ${ay - 0.15 * s - amp} ${x + w * 0.75} ${ay - 0.15 * s + amp} ${x + w} ${ay - 0.15 * s}`,
          fill: false,
          thick: t,
        },
        { path: hline(x, x + w, ay + 0.05 * s), fill: false, thick: t },
        { path: hline(x, x + w, ay + 0.18 * s), fill: false, thick: t },
      ],
    };
  },
  propto: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    // r = w/4 ensures exact tangency: left circle right edge = right arc left edge = x+2r
    const r = w / 4;
    return {
      w,
      h: (AX + 0.22) * s,
      d: 0.22 * s,
      parts: [
        // Left circle: center (x+r, ay), spans x to x+2r
        {
          path: `M ${x} ${ay} A ${r} ${r} 0 1 0 ${x + 2 * r} ${ay} A ${r} ${r} 0 1 0 ${x} ${ay} Z`,
          fill: false,
          thick: t,
        },
        // Right arc: ⊃ shape from (x+2r, ay-r) curving right to (x+w, ay) back to (x+2r, ay+r)
        {
          path: `M ${x + 2 * r} ${ay - r} C ${x + w} ${ay - r} ${x + w} ${ay + r} ${x + 2 * r} ${ay + r}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  ll: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.22 * s;
    return {
      w,
      h: AX * s + h,
      d: h,
      parts: [
        {
          path: `M ${x + w * 0.5} ${ay - h} L ${x} ${ay} L ${x + w * 0.5} ${ay + h}`,
          fill: false,
          thick: t,
        },
        {
          path: `M ${x + w} ${ay - h} L ${x + w * 0.5} ${ay} L ${x + w} ${ay + h}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  gg: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.22 * s;
    return {
      w,
      h: AX * s + h,
      d: h,
      parts: [
        { path: `M ${x} ${ay - h} L ${x + w * 0.5} ${ay} L ${x} ${ay + h}`, fill: false, thick: t },
        {
          path: `M ${x + w * 0.5} ${ay - h} L ${x + w} ${ay} L ${x + w * 0.5} ${ay + h}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  prec: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    return {
      w,
      h: AX * s + h,
      d: h,
      parts: [
        {
          path: `M ${x + w} ${ay - h} C ${x} ${ay - h} ${x} ${ay + h} ${x + w} ${ay + h}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  succ: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    return {
      w,
      h: AX * s + h,
      d: h,
      parts: [
        {
          path: `M ${x} ${ay - h} C ${x + w} ${ay - h} ${x + w} ${ay + h} ${x} ${ay + h}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },

  // -------------------------------------------------------------------------
  // Set theory
  // -------------------------------------------------------------------------

  in: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    return {
      w,
      h: AX * s + h,
      d: h,
      parts: [
        {
          path: `M ${x + w} ${ay - h} Q ${x} ${ay - h} ${x} ${ay} Q ${x} ${ay + h} ${x + w} ${ay + h}`,
          fill: false,
          thick: t,
        },
        { path: hline(x, x + w, ay), fill: false, thick: t },
      ],
    };
  },
  notin: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    return {
      w,
      h: AX * s + h,
      d: h,
      parts: [
        {
          path: `M ${x + w} ${ay - h} Q ${x} ${ay - h} ${x} ${ay} Q ${x} ${ay + h} ${x + w} ${ay + h}`,
          fill: false,
          thick: t,
        },
        { path: hline(x, x + w, ay), fill: false, thick: t },
        {
          path: `M ${x + 0.1 * s} ${b + 0.05 * s} L ${x + w - 0.1 * s} ${b - 0.65 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  ni: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    return {
      w,
      h: AX * s + h,
      d: h,
      parts: [
        {
          path: `M ${x} ${ay - h} Q ${x + w} ${ay - h} ${x + w} ${ay} Q ${x + w} ${ay + h} ${x} ${ay + h}`,
          fill: false,
          thick: t,
        },
        { path: hline(x, x + w, ay), fill: false, thick: t },
      ],
    };
  },
  subset: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    return {
      w,
      h: AX * s + h,
      d: h,
      parts: [
        {
          path: `M ${x + w} ${ay - h} Q ${x} ${ay - h} ${x} ${ay} Q ${x} ${ay + h} ${x + w} ${ay + h}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  supset: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    return {
      w,
      h: AX * s + h,
      d: h,
      parts: [
        {
          path: `M ${x} ${ay - h} Q ${x + w} ${ay - h} ${x + w} ${ay} Q ${x + w} ${ay + h} ${x} ${ay + h}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  subseteq: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    const bar = 0.08 * s;
    return {
      w,
      h: AX * s + h,
      d: h + bar + 0.05 * s,
      parts: [
        {
          path: `M ${x + w} ${ay - h} Q ${x} ${ay - h} ${x} ${ay} Q ${x} ${ay + h} ${x + w} ${ay + h}`,
          fill: false,
          thick: t,
        },
        { path: hline(x, x + w, ay + h + bar), fill: false, thick: t },
      ],
    };
  },
  supseteq: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = WT * s;
    const h = 0.23 * s;
    const bar = 0.08 * s;
    return {
      w,
      h: AX * s + h,
      d: h + bar + 0.05 * s,
      parts: [
        {
          path: `M ${x} ${ay - h} Q ${x + w} ${ay - h} ${x + w} ${ay} Q ${x + w} ${ay + h} ${x} ${ay + h}`,
          fill: false,
          thick: t,
        },
        { path: hline(x, x + w, ay + h + bar), fill: false, thick: t },
      ],
    };
  },
  cap: (x, b, s) => {
    const t = T * s;
    const w = 0.65 * s;
    const r = w / 2;
    const top = b - 0.7 * s;
    const legBot = b;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [
        {
          path: `M ${x} ${legBot} V ${top + r} A ${r} ${r} 0 0 1 ${x + w} ${top + r} V ${legBot}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  cup: (x, b, s) => {
    const t = T * s;
    const w = 0.65 * s;
    const r = w / 2;
    const top = b - 0.7 * s;
    const legBot = b;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [
        {
          path: `M ${x} ${top} V ${legBot - r} A ${r} ${r} 0 0 0 ${x + w} ${legBot - r} V ${top}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  emptyset: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const r = 0.25 * s;
    const cx = x + r;
    return {
      w: 2 * r,
      h: (AX + r + 0.02) * s,
      d: (r - AX + 0.02) * s,
      parts: [
        {
          path: `M ${cx - r} ${ay} A ${r} ${r} 0 1 0 ${cx + r} ${ay} A ${r} ${r} 0 1 0 ${cx - r} ${ay} Z`,
          fill: false,
          thick: t,
        },
        {
          path: `M ${cx - r * 0.75} ${ay + r + 0.08 * s} L ${cx + r * 0.75} ${ay - r - 0.08 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  setminus: (x, b, s) => {
    const t = T * s;
    const w = 0.4 * s;
    return {
      w,
      h: 0.65 * s,
      d: 0.05 * s,
      parts: [{ path: `M ${x} ${b - 0.65 * s} L ${x + w} ${b}`, fill: false, thick: t }],
    };
  },

  // -------------------------------------------------------------------------
  // Logic / quantifiers
  // -------------------------------------------------------------------------

  forall: (x, b, s) => {
    const t = T * s;
    const w = 0.6 * s;
    const top = b - 0.7 * s;
    const mid = b - 0.38 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [
        {
          path: `M ${x + 0.04 * s} ${b} L ${x + w / 2} ${top} L ${x + w - 0.04 * s} ${b}`,
          fill: false,
          thick: t,
        },
        { path: hline(x + 0.16 * s, x + w - 0.16 * s, mid), fill: false, thick: t },
      ],
    };
  },
  exists: (x, b, s) => {
    const t = T * s;
    const w = 0.48 * s;
    const top = b - 0.7 * s;
    const mid = (top + b) / 2;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [
        { path: `M ${x} ${top} V ${b}`, fill: false, thick: t },
        { path: hline(x, x + w, top), fill: false, thick: t },
        { path: hline(x, x + w * 0.85, mid), fill: false, thick: t },
        { path: hline(x, x + w, b), fill: false, thick: t },
      ],
    };
  },
  nexists: (x, b, s) => {
    const t = T * s;
    const w = 0.52 * s;
    const top = b - 0.7 * s;
    const mid = (top + b) / 2;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [
        { path: `M ${x} ${top} V ${b}`, fill: false, thick: t },
        { path: hline(x, x + w * 0.9, top), fill: false, thick: t },
        { path: hline(x, x + w * 0.8, mid), fill: false, thick: t },
        { path: hline(x, x + w * 0.9, b), fill: false, thick: t },
        {
          path: `M ${x + 0.1 * s} ${b + 0.05 * s} L ${x + w - 0.1 * s} ${top - 0.05 * s}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  wedge: (x, b, s) => {
    const t = T * s;
    const w = 0.6 * s;
    const top = b - 0.65 * s;
    return {
      w,
      h: 0.65 * s,
      d: 0.05 * s,
      parts: [{ path: `M ${x} ${b} L ${x + w / 2} ${top} L ${x + w} ${b}`, fill: false, thick: t }],
    };
  },
  vee: (x, b, s) => {
    const t = T * s;
    const w = 0.6 * s;
    const top = b - 0.65 * s;
    return {
      w,
      h: 0.65 * s,
      d: 0.05 * s,
      parts: [
        { path: `M ${x} ${top} L ${x + w / 2} ${b} L ${x + w} ${top}`, fill: false, thick: t },
      ],
    };
  },
  top: (x, b, s) => {
    const t = T * s;
    const w = 0.55 * s;
    const top = b - 0.7 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [
        { path: hline(x, x + w, top), fill: false, thick: t },
        { path: `M ${x + w / 2} ${top} V ${b}`, fill: false, thick: t },
      ],
    };
  },
  bot: (x, b, s) => {
    const t = T * s;
    const w = 0.55 * s;
    return {
      w,
      h: 0.65 * s,
      d: 0.05 * s,
      parts: [
        { path: hline(x, x + w, b), fill: false, thick: t },
        { path: `M ${x + w / 2} ${b - 0.65 * s} V ${b}`, fill: false, thick: t },
      ],
    };
  },

  // -------------------------------------------------------------------------
  // Misc math
  // -------------------------------------------------------------------------

  infty: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const w = 0.8 * s;
    const cx = x + w / 2;
    const rx = 0.18 * s;
    const ry = 0.16 * s;
    return {
      w,
      h: (AX + ry + 0.02) * s,
      d: (ry - AX + 0.02) * s,
      parts: [
        {
          path: [
            `M ${cx} ${ay}`,
            `C ${cx + rx * 0.5} ${ay - ry} ${cx + rx * 1.5} ${ay - ry} ${cx + rx * 2} ${ay}`,
            `C ${cx + rx * 1.5} ${ay + ry} ${cx + rx * 0.5} ${ay + ry} ${cx} ${ay}`,
            `C ${cx - rx * 0.5} ${ay - ry} ${cx - rx * 1.5} ${ay - ry} ${cx - rx * 2} ${ay}`,
            `C ${cx - rx * 1.5} ${ay + ry} ${cx - rx * 0.5} ${ay + ry} ${cx} ${ay}`,
          ].join(' '),
          fill: false,
          thick: t,
        },
      ],
    };
  },
  partial: (x, b, s) => {
    const t = T * s * 0.9;
    const w = 0.45 * s;
    const r = 0.18 * s;
    const cx = x + 0.22 * s;
    const cy = b - r;
    const top = b - 0.72 * s;
    return {
      w,
      h: 0.72 * s,
      d: 0.05 * s,
      parts: [
        // bottom loop
        {
          path: `M ${cx + r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx + r} ${cy} Z`,
          fill: false,
          thick: t,
        },
        // upper stroke
        {
          path: `M ${cx + r} ${cy} C ${cx + r} ${cy - 0.25 * s} ${cx + 0.1 * s} ${top} ${cx - 0.1 * s} ${top}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  nabla: (x, b, s) => {
    const t = T * s;
    const w = 0.7 * s;
    const top = b - 0.68 * s;
    return {
      w,
      h: 0.68 * s,
      d: 0.02 * s,
      parts: [{ path: `M ${x} ${top} H ${x + w} L ${x + w / 2} ${b} Z`, fill: false, thick: t }],
    };
  },
  oplus: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const r = 0.26 * s;
    const cx = x + r;
    return {
      w: 2 * r,
      h: (AX + r) * s,
      d: (r - AX + 0.02) * s,
      parts: [
        {
          path: `M ${cx - r} ${ay} A ${r} ${r} 0 1 0 ${cx + r} ${ay} A ${r} ${r} 0 1 0 ${cx - r} ${ay} Z`,
          fill: false,
          thick: t,
        },
        { path: `M ${cx} ${ay - r} V ${ay + r}`, fill: false, thick: t },
        { path: hline(cx - r, cx + r, ay), fill: false, thick: t },
      ],
    };
  },
  ominus: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const r = 0.26 * s;
    const cx = x + r;
    return {
      w: 2 * r,
      h: (AX + r) * s,
      d: (r - AX + 0.02) * s,
      parts: [
        {
          path: `M ${cx - r} ${ay} A ${r} ${r} 0 1 0 ${cx + r} ${ay} A ${r} ${r} 0 1 0 ${cx - r} ${ay} Z`,
          fill: false,
          thick: t,
        },
        { path: hline(cx - r, cx + r, ay), fill: false, thick: t },
      ],
    };
  },
  otimes: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const r = 0.26 * s;
    const cx = x + r;
    const d = r * 0.7;
    return {
      w: 2 * r,
      h: (AX + r) * s,
      d: (r - AX + 0.02) * s,
      parts: [
        {
          path: `M ${cx - r} ${ay} A ${r} ${r} 0 1 0 ${cx + r} ${ay} A ${r} ${r} 0 1 0 ${cx - r} ${ay} Z`,
          fill: false,
          thick: t,
        },
        { path: `M ${cx - d} ${ay - d} L ${cx + d} ${ay + d}`, fill: false, thick: t },
        { path: `M ${cx + d} ${ay - d} L ${cx - d} ${ay + d}`, fill: false, thick: t },
      ],
    };
  },
  odot: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const r = 0.26 * s;
    const cx = x + r;
    const dr = 0.05 * s;
    return {
      w: 2 * r,
      h: (AX + r) * s,
      d: (r - AX + 0.02) * s,
      parts: [
        {
          path: `M ${cx - r} ${ay} A ${r} ${r} 0 1 0 ${cx + r} ${ay} A ${r} ${r} 0 1 0 ${cx - r} ${ay} Z`,
          fill: false,
          thick: t,
        },
        {
          path: `M ${cx - dr} ${ay} A ${dr} ${dr} 0 1 0 ${cx + dr} ${ay} A ${dr} ${dr} 0 1 0 ${cx - dr} ${ay} Z`,
          fill: true,
          thick: 0,
        },
      ],
    };
  },
  parallel: (x, b, s) => {
    const t = T * s;
    const w = 0.35 * s;
    const top = b - 0.7 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [
        { path: `M ${x + w * 0.25} ${top} V ${b}`, fill: false, thick: t },
        { path: `M ${x + w * 0.75} ${top} V ${b}`, fill: false, thick: t },
      ],
    };
  },
  perp: (x, b, s) => {
    const t = T * s;
    const w = 0.55 * s;
    const top = b - 0.7 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [
        { path: hline(x, x + w, b), fill: false, thick: t },
        { path: `M ${x + w / 2} ${b} V ${top}`, fill: false, thick: t },
      ],
    };
  },
  angle: (x, b, s) => {
    const t = T * s;
    const w = 0.55 * s;
    const top = b - 0.7 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0.02 * s,
      parts: [
        { path: `M ${x} ${b} L ${x + w} ${top}`, fill: false, thick: t },
        { path: hline(x, x + w, b), fill: false, thick: t },
        // Small arc to indicate angle
        {
          path: `M ${x + 0.2 * s} ${b} A ${0.18 * s} ${0.18 * s} 0 0 0 ${x + 0.18 * s} ${b - 0.08 * s}`,
          fill: false,
          thick: t * 0.8,
        },
      ],
    };
  },
  therefore: (x, b, s) => {
    const t = T * s;
    const w = 0.5 * s;
    const r = 0.06 * s;
    const ay = b - AX * s;
    const cx = x + w / 2;
    function dot(dx: number, dy: number) {
      return `M ${cx + dx - r} ${ay + dy} A ${r} ${r} 0 1 0 ${cx + dx + r} ${ay + dy} A ${r} ${r} 0 1 0 ${cx + dx - r} ${ay + dy} Z`;
    }
    return {
      w,
      h: (AX + 0.22) * s,
      d: 0.22 * s,
      parts: [
        { path: dot(-w * 0.3, 0.18 * s), fill: true, thick: 0 },
        { path: dot(+w * 0.3, 0.18 * s), fill: true, thick: 0 },
        { path: dot(0, -0.18 * s), fill: true, thick: 0 },
      ],
    };
  },
  because: (x, b, s) => {
    const t = T * s;
    const w = 0.5 * s;
    const r = 0.06 * s;
    const ay = b - AX * s;
    const cx = x + w / 2;
    function dot(dx: number, dy: number) {
      return `M ${cx + dx - r} ${ay + dy} A ${r} ${r} 0 1 0 ${cx + dx + r} ${ay + dy} A ${r} ${r} 0 1 0 ${cx + dx - r} ${ay + dy} Z`;
    }
    return {
      w,
      h: (AX + 0.22) * s,
      d: 0.22 * s,
      parts: [
        { path: dot(0, -0.18 * s), fill: true, thick: 0 },
        { path: dot(-w * 0.3, 0.0), fill: true, thick: 0 },
        { path: dot(+w * 0.3, 0.0), fill: true, thick: 0 },
      ],
    };
  },
  cdots: (x, b, s) => {
    const ay = b - AX * s;
    const t = T * s;
    const r = 0.07 * s;
    const gap = 0.22 * s;
    function dot(cx: number) {
      return `M ${cx - r} ${ay} A ${r} ${r} 0 1 0 ${cx + r} ${ay} A ${r} ${r} 0 1 0 ${cx - r} ${ay} Z`;
    }
    const w = gap * 2 + r * 2;
    return {
      w,
      h: (AX + r) * s,
      d: (r - AX + 0.02) * s,
      parts: [
        { path: dot(x + r), fill: true, thick: 0 },
        { path: dot(x + r + gap), fill: true, thick: 0 },
        { path: dot(x + r + gap * 2), fill: true, thick: 0 },
      ],
    };
  },
  vdots: (x, b, s) => {
    const t = T * s;
    const r = 0.06 * s;
    const gap = 0.2 * s;
    const cx = x + r;
    function dot(cy: number) {
      return `M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy} Z`;
    }
    const top = b - 0.65 * s;
    return {
      w: r * 2,
      h: 0.65 * s,
      d: 0.05 * s,
      parts: [
        { path: dot(top), fill: true, thick: 0 },
        { path: dot(top + gap), fill: true, thick: 0 },
        { path: dot(top + gap * 2), fill: true, thick: 0 },
      ],
    };
  },
  ddots: (x, b, s) => {
    const r = 0.06 * s;
    const gap = 0.2 * s;
    function dot(cx: number, cy: number) {
      return `M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy} Z`;
    }
    const w = gap * 2 + r * 2;
    const top = b - 0.65 * s;
    return {
      w,
      h: 0.65 * s,
      d: 0.05 * s,
      parts: [
        { path: dot(x + r, top), fill: true, thick: 0 },
        { path: dot(x + r + gap, top + gap), fill: true, thick: 0 },
        { path: dot(x + r + gap * 2, top + gap * 2), fill: true, thick: 0 },
      ],
    };
  },

  // -------------------------------------------------------------------------
  // Large operators
  // -------------------------------------------------------------------------

  sum: (x, b, s) => {
    const t = 0.065 * s;
    const w = 0.72 * s;
    const top = b - 0.8 * s;
    const bot = b + 0.1 * s;
    const mid = (top + bot) / 2;
    const indent = 0.06 * s;
    return {
      w,
      h: 0.8 * s,
      d: 0.1 * s,
      parts: [
        { path: `M ${x + w - indent} ${top} L ${x} ${top}`, fill: false, thick: t },
        { path: `M ${x} ${top} L ${x + w / 2} ${mid} L ${x} ${bot}`, fill: false, thick: t },
        { path: `M ${x + w - indent} ${bot} L ${x} ${bot}`, fill: false, thick: t },
      ],
    };
  },
  prod: (x, b, s) => {
    // Two L-shaped paths (proper miter at each corner) + center bar
    const t = 0.065 * s;
    const w = 0.7 * s;
    const legT = 0.09 * s;
    const top = b - 0.75 * s;
    const bot = b + 0.08 * s;
    return {
      w,
      h: 0.75 * s,
      d: 0.08 * s,
      parts: [
        { path: `M ${x} ${top} H ${x + legT} V ${bot}`, fill: false, thick: t },
        { path: `M ${x + w} ${top} H ${x + w - legT} V ${bot}`, fill: false, thick: t },
        { path: `M ${x + legT} ${top} H ${x + w - legT}`, fill: false, thick: t },
      ],
    };
  },
  coprod: (x, b, s) => {
    // ∐ is upside-down ∏ — same L-path approach, legs point up from bottom bar
    const t = 0.065 * s;
    const w = 0.7 * s;
    const legT = 0.09 * s;
    const top = b - 0.8 * s;
    const bot = b + 0.05 * s;
    return {
      w,
      h: 0.8 * s,
      d: 0.05 * s,
      parts: [
        { path: `M ${x} ${bot} H ${x + legT} V ${top}`, fill: false, thick: t },
        { path: `M ${x + w} ${bot} H ${x + w - legT} V ${top}`, fill: false, thick: t },
        { path: `M ${x + legT} ${bot} H ${x + w - legT}`, fill: false, thick: t },
      ],
    };
  },
  int: (x, b, s) => {
    const t = 0.06 * s; // Stroke thickness
    const r = 0.1 * s; // Radius of the semicircle hooks
    const w = r * 3; // Bounding width
    const top = b - 0.85 * s; // Absolute top boundary
    const bot = b + 0.15 * s; // Absolute bottom boundary
    const cx = x + w / 2; // The vertical spine axis

    return {
      w,
      h: 0.85 * s,
      d: 0.15 * s,
      parts: [
        {
          path: [
            // 1. Start at the top-right tip (pointing down)
            `M ${cx + 2 * r} ${top + r}`,
            // 2. Top Arc: Move Left, Sweep 0 (curves UP/CCW) to the spine
            `A ${r} ${r} 0 0 0 ${cx} ${top + r}`,
            // 3. The strictly vertical spine
            `L ${cx} ${bot - r}`,
            // 4. Bottom Arc: Move Left, Sweep 1 (curves DOWN/CW) to the bottom-left tip
            `A ${r} ${r} 0 0 1 ${cx - 2 * r} ${bot - r}`,
          ].join(' '),
          fill: false,
          thick: t,
        },
      ],
    };
  },
  oint: (x, b, s) => {
    // Contour integral: ∮ = ∫ with a small circle in the middle
    const base = DRAWN_SYMBOLS.int(x, b, s);
    const cx = x + 0.21 * s;
    const ay = b - AX * s;
    const r = 0.09 * s;
    const t = 0.04 * s;
    base.parts.push({
      path: `M ${cx - r} ${ay} A ${r} ${r} 0 1 0 ${cx + r} ${ay} A ${r} ${r} 0 1 0 ${cx - r} ${ay} Z`,
      fill: false,
      thick: t,
    });
    return base;
  },
  iint: (x, b, s) => {
    // Two integrals overlapping slightly; total width = int.w + offset
    const sh1 = DRAWN_SYMBOLS.int(x, b, s);
    const sh2 = DRAWN_SYMBOLS.int(x + 0.18 * s, b, s);
    return { w: 0.6 * s, h: sh1.h, d: sh1.d, parts: [...sh1.parts, ...sh2.parts] };
  },
  bigcap: (x, b, s) => DRAWN_SYMBOLS.cap(x, b, s),
  bigcup: (x, b, s) => DRAWN_SYMBOLS.cup(x, b, s),

  // -------------------------------------------------------------------------
  // Delimiter-style (drawn large by delimited layout, but registered here for symbol)
  // -------------------------------------------------------------------------

  lfloor: (x, b, s) => {
    const t = T * s;
    const w = 0.2 * s;
    const top = b - 0.7 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [{ path: `M ${x} ${top} V ${b} H ${x + w}`, fill: false, thick: t }],
    };
  },
  rfloor: (x, b, s) => {
    const t = T * s;
    const w = 0.2 * s;
    const top = b - 0.7 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [{ path: `M ${x + w} ${top} V ${b} H ${x}`, fill: false, thick: t }],
    };
  },
  lceil: (x, b, s) => {
    const t = T * s;
    const w = 0.2 * s;
    const top = b - 0.7 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [{ path: `M ${x} ${b} V ${top} H ${x + w}`, fill: false, thick: t }],
    };
  },
  rceil: (x, b, s) => {
    const t = T * s;
    const w = 0.2 * s;
    const top = b - 0.7 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [{ path: `M ${x + w} ${b} V ${top} H ${x}`, fill: false, thick: t }],
    };
  },
  langle: (x, b, s) => {
    const t = T * s;
    const w = 0.3 * s;
    const top = b - 0.7 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [
        {
          path: `M ${x + w} ${top} L ${x} ${(top + b) / 2} L ${x + w} ${b}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  rangle: (x, b, s) => {
    const t = T * s;
    const w = 0.3 * s;
    const top = b - 0.7 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0.05 * s,
      parts: [
        { path: `M ${x} ${top} L ${x + w} ${(top + b) / 2} L ${x} ${b}`, fill: false, thick: t },
      ],
    };
  },
  // Bra-ket notation
  lvert: (x, b, s) => {
    const t = T * s * 0.8;
    const w = 0.12 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0,
      parts: [{ path: `M ${x + w / 2} ${b - 0.7 * s} V ${b + 0.1 * s}`, fill: false, thick: t }],
    };
  },
  rvert: (x, b, s) => {
    const t = T * s * 0.8;
    const w = 0.12 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0,
      parts: [{ path: `M ${x + w / 2} ${b - 0.7 * s} V ${b + 0.1 * s}`, fill: false, thick: t }],
    };
  },
  lVert: (x, b, s) => {
    const t = T * s * 0.8;
    const w = 0.25 * s;
    const gap = 0.08 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0,
      parts: [
        { path: `M ${x + gap} ${b - 0.7 * s} V ${b + 0.1 * s}`, fill: false, thick: t },
        { path: `M ${x + w - gap} ${b - 0.7 * s} V ${b + 0.1 * s}`, fill: false, thick: t },
      ],
    };
  },
  rVert: (x, b, s) => {
    const t = T * s * 0.8;
    const w = 0.25 * s;
    const gap = 0.08 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0,
      parts: [
        { path: `M ${x + gap} ${b - 0.7 * s} V ${b + 0.1 * s}`, fill: false, thick: t },
        { path: `M ${x + w - gap} ${b - 0.7 * s} V ${b + 0.1 * s}`, fill: false, thick: t },
      ],
    };
  },
  // Operator symbols
  triangle: (x, b, s) => {
    const t = T * s;
    const w = 0.5 * s;
    const h = 0.45 * s;
    return {
      w,
      h: h + 0.05 * s,
      d: 0.05 * s,
      parts: [
        { path: `M ${x + w / 2} ${b - h} L ${x} ${b} L ${x + w} ${b} Z`, fill: true, thick: 0 },
      ],
    };
  },
  square: (x, b, s) => {
    const t = T * s;
    const w = 0.45 * s;
    const h = 0.45 * s;
    return {
      w,
      h: h + 0.05 * s,
      d: 0.05 * s,
      parts: [{ path: `M ${x} ${b - h} H ${x + w} V ${b} H ${x} Z`, fill: true, thick: 0 }],
    };
  },
  checkmark: (x, b, s) => {
    const t = T * s;
    const w = 0.45 * s;
    const h = 0.4 * s;
    return {
      w,
      h: h + 0.05 * s,
      d: 0.05 * s,
      parts: [
        {
          path: `M ${x + 0.1 * s} ${b - 0.35 * s} L ${x + 0.35 * s} ${b} L ${x + w} ${b - h}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
  bullet: (x, b, s) => {
    const r = 0.15 * s;
    return {
      w: r * 2,
      h: r + 0.05 * s,
      d: r - 0.05 * s,
      parts: [
        {
          path: `M ${x + r} ${b - r * 0.3} A ${r} ${r} 0 1 1 ${x + r} ${b - r * 0.31} Z`,
          fill: true,
          thick: 0,
        },
      ],
    };
  },

  // -------------------------------------------------------------------------
  // sqrt (standalone symbol, not the built-in sqrt node)
  // -------------------------------------------------------------------------

  sqrtsym: (x, b, s) => {
    const t = 0.05 * s;
    const w = 0.45 * s;
    const top = b - 0.7 * s;
    const mid = b - 0.35 * s;
    return {
      w,
      h: 0.7 * s,
      d: 0,
      parts: [
        {
          path: `M ${x} ${mid} L ${x + w * 0.3} ${b} L ${x + w * 0.7} ${top} H ${x + w}`,
          fill: false,
          thick: t,
        },
      ],
    };
  },
};

// ∓ (minus-plus): like ± but flipped
DRAWN_SYMBOLS.mp = (x, b, s) => {
  const ay = b - AX * s;
  const t = T * s;
  const hw = 0.25 * s;
  const hv = 0.25 * s;
  return {
    w: hw * 2,
    h: (AX + hv + 0.05) * s,
    d: (hv + 0.1) * s,
    parts: [
      // minus on top
      { path: hline(x, x + hw * 2, ay - hv), fill: false, thick: t },
      // plus on bottom: horizontal + vertical
      { path: hline(x, x + hw * 2, ay + hv * 0.3), fill: false, thick: t },
      { path: `M ${x + hw} ${ay} V ${ay + hv * 2 * 0.7}`, fill: false, thick: t },
    ],
  };
};

// Aliases
DRAWN_SYMBOLS.to = DRAWN_SYMBOLS.rightarrow;
DRAWN_SYMBOLS.gets = DRAWN_SYMBOLS.leftarrow;
DRAWN_SYMBOLS.implies = DRAWN_SYMBOLS.Rightarrow;
DRAWN_SYMBOLS.iff = DRAWN_SYMBOLS.Leftrightarrow;
DRAWN_SYMBOLS.land = DRAWN_SYMBOLS.wedge;
DRAWN_SYMBOLS.lor = DRAWN_SYMBOLS.vee;
DRAWN_SYMBOLS.varnothing = DRAWN_SYMBOLS.emptyset;
DRAWN_SYMBOLS.geqslant = DRAWN_SYMBOLS.geq;
DRAWN_SYMBOLS.leqslant = DRAWN_SYMBOLS.leq;
DRAWN_SYMBOLS.ne = DRAWN_SYMBOLS.neq;
DRAWN_SYMBOLS.nsubseteq = (x, b, s) => {
  const sh = DRAWN_SYMBOLS.subseteq(x, b, s);
  const t = T * s;
  sh.parts.push({
    path: `M ${x + 0.05 * s} ${b + 0.1 * s} L ${x + 0.55 * s} ${b - 0.7 * s}`,
    fill: false,
    thick: t,
  });
  return sh;
};

// ---------------------------------------------------------------------------
// Draw a symbol on a PageGen
// ---------------------------------------------------------------------------

export function drawSymbol(sym: SymbolShape, page: PageGen, brush: PdfBrush): void {
  for (const part of sym.parts) {
    if (!part.path) continue;
    page.page.moveTo(0, page.page.getHeight());
    page.page.drawSvgPath(part.path, {
      color: part.fill ? brush.color.toPdfLibColor() : undefined,
      borderColor: part.fill ? undefined : brush.color.toPdfLibColor(),
      borderWidth: part.fill ? 0 : part.thick,
      opacity: brush.color.opacity,
    });
    page.page.moveTo(0, 0);
  }
}
